import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Box } from '@mui/material'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import {
  fetchGroups,
  selectGroupRequests,
  selectGroups,
  selectIsLoading,
  selectMessage,
  selectPage,
  selectSeverity,
  selectShouldOpen,
  selectTotalPages,
  setOpen,
} from '../../../../store/socialSlice'
import { useDevice } from '../../../../hooks/useDevice'
import { useWebLayoutContext } from '../../../../context/WebLayoutContext'
import { Group } from '../../../../types/groupTypes'
import ImgIcon from '../../../../components/ui/ImgIcon'
import SocialGroup from './SocialGroup'
import {
  SocialEmptyScreenButton,
  SocialEmptyScreenContainer,
  SocialEmptyScreenIcon,
  SocialNewGroupTypography,
  SocialSkeleton,
} from '../styled/Social.styled'
import EmptyScreen from '../../../../components/EmptyScreen'
import GroupAddEditDialog from './GroupAddEditDialog'
import { MuiAlert, MuiSnackbar } from '../../styled/global.styled'
import { IconPlus } from '@tabler/icons-react'
interface IGroupTabProp {
  isModalOpen: boolean
  setOpenAddModel: React.Dispatch<React.SetStateAction<boolean>>
  /** Launches the unified share-link create flow (/dine) instead of the email-invite dialog. */
  onCreateGroup?: () => void
}
const GroupTab: React.FC<IGroupTabProp> = ({
  setOpenAddModel,
  isModalOpen,
  onCreateGroup,
}) => {
  const { theme, dispatch, isSmallScreen } = useAppUtilities()
  const groups = useAppSelector(selectGroups)
  const groupRequests = useAppSelector(selectGroupRequests)
  const isLoading = useAppSelector(selectIsLoading)
  const openServer = useAppSelector(selectShouldOpen)
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const totalPages = useAppSelector(selectTotalPages)
  const page = useAppSelector(selectPage)
  const { isHybrid } = useDevice()
  const { isScrollBottom } = useWebLayoutContext()

  const [groupList, setGroupList] = useState<Group[]>(groups ?? [])

  const didInit = useRef(false);
  const loadingRef = useRef(false);

  const fetchItems = useCallback(async (pageNumber: number) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      await dispatch(fetchGroups(pageNumber));
    } finally {
      loadingRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (didInit.current) return;      // <-- run once per mount
    didInit.current = true;

    // Always refetch page 0 on mount. The previous `length === 0` guard skipped the fetch whenever
    // the Redux cache was warm, so a group saved elsewhere (e.g. graduating a dining session via
    // "Save this group") never appeared until a hard reload. The page-0 reducer replaces the list,
    // so this is a safe refresh — the cached list stays rendered while it revalidates.
    void fetchItems(0);
  }, [fetchItems]);

  useEffect(() => {
    setGroupList(groups)
  }, [groups])

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  useEffect(() => {
    if (isScrollBottom && totalPages - 1 > page && !loadingRef.current) {
      void fetchItems(page + 1);
    }
  }, [isScrollBottom, totalPages, page, fetchItems]);

  return (
    <Box>
      <div
        style={{
          position: 'relative',
        }}
      >
        {isSmallScreen && (
          <ImgIcon
            alt="create-resactly-group"
            iconElement={
              <IconPlus
                size={18}
                stroke={2}
                color={theme.palette.background.paper}
              />
            }
            wrapperStyle={{
              backgroundColor: theme.palette.primary.light,
              position: 'fixed',
              bottom: isHybrid
                ? `calc(84px + env(safe-area-inset-bottom))`
                : '20px',
              right: isHybrid ? '16px' : '20px',
              boxShadow: '0px 0px 12px 0px #00000059',
              zIndex: 10,
            }}
            onClick={() =>
              onCreateGroup ? onCreateGroup() : setOpenAddModel(true)
            }
          />
        )}
        {(groupList.length !== 0 || groupRequests.length !== 0) && (
          <SocialGroup
            isLoader={isLoading}
            groups={groupList}
            groupRequests={groupRequests}
            page={page}
          />
        )}

        {isLoading && groupList.length < 1 && (
          <SocialSkeleton variant="rounded" height={98} />
        )}

        {!isLoading && groupList.length === 0 && groupRequests.length === 0 && (
          <EmptyScreen
            containerHeight="calc(100vh - 296px)"
            imageUrl="/images/empty-screen.svg"
            mainText="No Groups Yet"
            secondaryText={`Looks like you haven't created or joined any group Groups allow you to connect with others to discover collective dining recommendations`}
          >
            <SocialEmptyScreenContainer>
              <SocialEmptyScreenButton
                variant="text"
                onClick={() =>
              onCreateGroup ? onCreateGroup() : setOpenAddModel(true)
            }
              >
                <SocialEmptyScreenIcon stroke={2} size={18} />
                <SocialNewGroupTypography>New Group</SocialNewGroupTypography>
              </SocialEmptyScreenButton>
            </SocialEmptyScreenContainer>
          </EmptyScreen>
        )}
      </div>

      <GroupAddEditDialog
        groupAction="add"
        openModel={isModalOpen}
        setIsModalOpen={setOpenAddModel}
      ></GroupAddEditDialog>
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </Box>
  )
}

export default GroupTab
